import { setCompanyLogo } from './marketing_preview/dom'
import { reviewDetails, type BoardJob } from './marketing_preview/mock/data'

type Review = keyof typeof reviewDetails

export function createMarketingDashboard(dashboard: HTMLElement, root: HTMLElement, getRecords: () => BoardJob[]) {
  const hour = new Date().getHours()
  dashboard.querySelector<HTMLElement>('[data-dashboard-greeting]')!.textContent =
    `${hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening'}, Alex.`

  let mode: 'overview' | 'focus' = 'overview'
  let selected: Review = 'linear'
  const reviewed = new Set<Review>()

  function update(): void {
    dashboard.querySelector<HTMLElement>('[data-dashboard-overview]')!.hidden = mode !== 'overview'
    dashboard.querySelector<HTMLElement>('[data-dashboard-focus]')!.hidden = mode !== 'focus'
    const modeButton = dashboard.querySelector<HTMLButtonElement>('[data-dashboard-mode]')!
    modeButton.dataset.dashboardMode = mode === 'focus' ? 'overview' : 'focus'
    modeButton.setAttribute('aria-label', `Switch to ${mode === 'focus' ? 'Overview' : 'Focus'}`)
    modeButton.textContent = mode === 'focus' ? '▦  Overview' : '◎  Focus'

    const detail = reviewDetails[selected]
    const focusLogo = dashboard.querySelector<HTMLElement>('[data-dashboard-focus-subject] [data-company-logo]')!
    focusLogo.hidden = selected === 'complete'
    if (selected !== 'complete') setCompanyLogo(root, focusLogo, selected.charAt(0).toUpperCase() + selected.slice(1))
    dashboard.querySelector<HTMLElement>('[data-dashboard-conversation]')!.hidden = selected !== 'linear'
    dashboard.querySelector<HTMLElement>('[data-dashboard-other-detail]')!.hidden = selected === 'linear'
    dashboard.querySelector<HTMLElement>('[data-dashboard-detail-body]')!.hidden = selected === 'linear'
    for (const [selector, value] of Object.entries({
      '[data-dashboard-detail-kicker]': detail.kicker,
      '[data-dashboard-detail-title]': detail.title,
      '[data-dashboard-detail-body]': detail.body,
      '[data-dashboard-detail-source]': detail.source,
      '[data-dashboard-detail-copy]': detail.copy
    })) {
      dashboard.querySelector<HTMLElement>(selector)!.textContent = value
    }
    dashboard.querySelectorAll<HTMLButtonElement>('[data-dashboard-select]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.dashboardSelect === selected))
    })
    dashboard.querySelector<HTMLElement>('[data-dashboard-reviewed-count]')!.textContent = `${reviewed.size} / 3`
    dashboard.querySelector<HTMLElement>('[data-dashboard-progress]')!.style.width = `${(reviewed.size / 3) * 100}%`
    const markButton = dashboard.querySelector<HTMLButtonElement>('[data-dashboard-mark-review]')!
    markButton.disabled = selected === 'complete'
    markButton.textContent =
      selected === 'complete' ? 'All reviewed' : reviewed.has(selected) ? 'Next unreviewed ↗' : 'Mark reviewed & next ↗'
  }

  function updateSaved(): void {
    const saved = getRecords().filter((record) => record.stage === 'interested')
    const count = saved.length
    dashboard.querySelector<HTMLElement>('[data-dashboard-saved-heading]')!.textContent = count
      ? `${count} ${count === 1 ? 'possibility' : 'possibilities'}. Your next move.`
      : 'No saved jobs yet.'
    dashboard.querySelector<HTMLElement>('[data-dashboard-saved-description]')!.textContent = count
      ? 'Saved doesn’t mean applied. Start with one when you’re ready.'
      : 'Save jobs from search to build your shortlist.'
    dashboard.querySelector<HTMLElement>('[data-dashboard-saved-count]')!.textContent =
      `${count} saved ${count === 1 ? 'role' : 'roles'}`
    dashboard.querySelector<HTMLElement>('[data-dashboard-saved-foot-count]')!.textContent =
      count > 2 ? `Showing 2 of ${count} saved jobs` : `${count} saved jobs · not yet applied`
    dashboard.querySelectorAll<HTMLElement>('[data-dashboard-saved-row]').forEach((row, index) => {
      const job = saved[index]
      row.hidden = !job
      if (!job) return
      setCompanyLogo(root, row.querySelector<HTMLElement>('[data-company-logo]')!, job.company)
      row.querySelector<HTMLElement>('strong')!.textContent = `${job.company} · ${job.title}`
      row.querySelector<HTMLElement>('small')!.textContent = [job.location, job.saved && `Saved ${job.saved}`]
        .filter(Boolean)
        .join(' · ')
    })
  }

  function handleClick(button: HTMLButtonElement): void {
    if (
      !button.dataset.dashboardMode &&
      !button.dataset.dashboardReview &&
      !button.dataset.dashboardSelect &&
      !button.hasAttribute('data-dashboard-mark-review')
    )
      return
    if (button.dataset.dashboardMode) mode = button.dataset.dashboardMode as 'overview' | 'focus'
    const review = button.dataset.dashboardReview || button.dataset.dashboardSelect
    if (review && review in reviewDetails) {
      selected = review as Review
      mode = 'focus'
    }
    if (button.hasAttribute('data-dashboard-mark-review')) {
      reviewed.add(selected)
      selected = (['linear', 'ramp', 'plaid'] as Review[]).find((key) => !reviewed.has(key)) || 'complete'
    }
    update()
  }

  update()
  return { handleClick, updateSaved }
}
